// ============================================================
// useAttackExecution - Full attack orchestration hook.
// Extracted from useCombat handleAttack (lines 669-1089).
//
// Handles: validation, countdown, ammo deduction, skill roll,
// jam check, hit generation with force fields, damage calc,
// attack re-roll (Blademaster / Sure Strike), defence prompt
// or direct applyHits.
//
// The pipeline itself lives in `./attack/`, one stage per module:
//   context  - the parameters with defaults, built once per render
//   swing    - handleAttack: refuse, arm, roll, jam, overheat, miss
//   shot     - a hit onward: DoS, hits, pen, template, who it lands on
//   victim   - one victim: avoid test, damage, reaction prompt or hits
//   outcome  - the miss / jam entry and its countdown
//   prompts  - the re-roll and Furious Assault answers
//   notes    - the special-note sentences (asserted verbatim)
//
// The last context of every actor this screen held is kept, so a prompt
// answered after an actor switch answers as whoever swung (attack/context
// `answeringContext`).
// ============================================================
import { useEffect, useRef } from 'react';
import { attackContext, answeringContext, type AttackContext } from './attack/context';
import { handleAttack } from './attack/swing';
import { handleRam, type RamPair } from './attack/ram';
import type { RamRuling } from '../engine/actions/ram';
import { handleRerollChoice, handleBonusAttackChoice } from './attack/prompts';
import type { UseAttackExecutionParams, UseAttackExecutionReturn } from './attack/types';

export type {
  PendingBonusAttack,
  PendingReroll,
  UseAttackExecutionParams,
  UseAttackExecutionReturn,
} from './attack/types';

export default function useAttackExecution(params: UseAttackExecutionParams): UseAttackExecutionReturn {
  const ctx = attackContext(params);
  // The context of the last committed render that acted as the swinger of a WAITING prompt - kept only while one
  // waits (at most the two prompts' actors), and let go once none does.
  const held = useRef(new Map<string, AttackContext>());
  useEffect(() => {
    const waiting = [ctx.pendingReroll?.actorId, ctx.pendingBonusAttack?.actorId].filter(Boolean);
    if (waiting.length === 0) held.current.clear();
    else if (ctx.actorId && waiting.includes(ctx.actorId)) held.current.set(ctx.actorId, ctx);
  });
  const answering = (actorId: string | null | undefined) =>
    answeringContext(ctx, actorId ? held.current.get(actorId) : undefined);
  return {
    handleAttack: () => handleAttack(ctx),
    handleRam: (ruling: RamRuling, pair: RamPair) => handleRam(ctx, ruling, pair),
    handleRerollChoice: (accept: boolean) => handleRerollChoice(answering(ctx.pendingReroll?.actorId), accept),
    handleBonusAttackChoice: (accept: boolean) =>
      handleBonusAttackChoice(answering(ctx.pendingBonusAttack?.actorId), accept),
  };
}
